import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Zap, X, Copy, Video, ExternalLink, Send, DoorOpen } from "lucide-react";
import { openMeetTracked, autoEndClass } from "@/lib/meetWindow";

/* Shown right after "Start your class" — the Meet already opened in a new
 * tab; this modal lets the teacher copy the link, re-open the Meet, send it
 * to students, or jump into the classroom hub. Closing the Meet TAB ends the
 * class automatically (see lib/meetWindow.js). */
export const InstantClassModal = ({ data, onClose, onEnded }) => {
  const meetUrl = data.meet_url || data.class?.meet_url || "";
  const classId = data.class?.id;
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null);
  const copy = async () => {
    try { await navigator.clipboard.writeText(meetUrl); toast.success("Meet link copied"); }
    catch { toast.error("Copy failed"); }
  };
  const sendToStudents = async () => {
    setSending(true);
    try {
      const { data: r } = await api.post(`/classes/${classId}/send-meet-link`, {});
      setSent(r);
      toast.success(`Sent — ${r.notified} notified, ${r.emailed} emailed`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send the link"); }
    finally { setSending(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="instant-class-modal">
      <div className="bg-white w-full max-w-lg rounded-lg border border-black/10 shadow-2xl p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-[#00897B] inline-flex items-center gap-1.5"><Zap size={12} /> your class · live</div>
          <button onClick={onClose} data-testid="instant-modal-close" className="text-neutral-400 hover:text-[#0A0C10]"><X size={18} /></button>
        </div>
        <h3 className="font-display font-bold text-xl">{data.class?.title || "Your class"}</h3>
        <p className="text-sm text-neutral-500 mt-1">Your Google Meet is ready (it opened in a new tab). When you close the Meet tab, the class ends automatically.</p>
        <p className="text-[11px] text-neutral-500 mt-2" data-testid="instant-record-hint">
          <strong className="text-[#00897B]">Want to record?</strong> Open the classroom hub below and hit the red <strong>Record</strong> button — pick the Meet tab (tick "Also share tab audio") and the session saves straight to your Recordings page.
        </p>
        <div className="mt-4 flex items-center gap-2 bg-neutral-50 border border-neutral-200 rounded-md px-3 py-2.5">
          <span className="text-xs font-mono truncate flex-1" data-testid="instant-meet-url">{meetUrl}</span>
          <button onClick={copy} data-testid="instant-copy-meet" className="text-xs font-bold px-2.5 py-1.5 rounded-md bg-[#0A0C10] text-white hover:bg-black shrink-0 inline-flex items-center gap-1"><Copy size={11} /> Copy</button>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2">
          <button
            onClick={() => openMeetTracked(meetUrl, () => autoEndClass(classId, { onDone: onEnded }), classId)}
            data-testid="instant-open-meet"
            className="inline-flex items-center justify-center gap-2 text-sm font-bold rounded-md py-2.5 bg-[#00897B] hover:bg-[#00A48F] text-white"
          >
            <Video size={14} /> Open Meet <ExternalLink size={12} />
          </button>
          <button
            onClick={sendToStudents}
            disabled={sending || !!sent}
            data-testid="instant-send-students"
            className="inline-flex items-center justify-center gap-2 text-sm font-bold rounded-md py-2.5 bg-[#FFD600] text-[#0A0C10] hover:brightness-105 disabled:opacity-60"
          >
            <Send size={14} /> {sent ? `Sent to ${sent.notified}` : sending ? "Sending…" : "Send to students"}
          </button>
        </div>
        <button
          onClick={() => window.open(`/live/${classId}`, "_blank", "noopener,noreferrer")}
          data-testid="instant-open-classroom"
          className="mt-2 w-full text-xs font-semibold text-neutral-500 hover:text-black py-2 inline-flex items-center justify-center gap-1.5"
        >
          <DoorOpen size={12} /> Open the classroom hub (board, quizzes, attendance)
        </button>
      </div>
    </div>
  );
};
